import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";

export interface AuthCompany {
  id: string;
  name: string;
  slug: string;
  domain?: string | null;
  logoUrl?: string | null;
  primaryColor?: string | null;
  accentColor?: string | null;
  headerTagline?: string | null;
}

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  role: string;
  roleLabel?: string;
  companyId?: string | null;
  company?: AuthCompany | null;
  permissions?: string[];
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, companySlug?: string) => Promise<{ ok: true } | { ok: false; message: string }>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const TOKEN_KEY = "rently_token";
const USER_KEY = "rently_user";
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  });
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const saved = localStorage.getItem(USER_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState<boolean>(() => !token || !user);

  const fetchMe = useCallback(async (activeToken: string) => {
    const res = await fetch("/api/auth/me", {
      headers: { Authorization: `Bearer ${activeToken}` },
    });
    if (res.status === 401) {
      throw new Error("Session expired");
    }
    if (!res.ok) {
      return null;
    }
    return (await res.json()) as AuthUser;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const me = await fetchMe(token);
        if (!cancelled && me) {
          setUser(me);
          try { localStorage.setItem(USER_KEY, JSON.stringify(me)); } catch {}
        }
      } catch (err: any) {
        if (!cancelled && err.message === "Session expired") {
          setToken(null);
          setUser(null);
          try { 
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
          } catch {}
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // React to token being cleared elsewhere (e.g. a 401 from apiRequest)
  useEffect(() => {
    function onUnauthorized() {
      setToken(null);
      setUser(null);
      try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      } catch {}
    }
    window.addEventListener("rently:unauthorized", onUnauthorized);
    return () => window.removeEventListener("rently:unauthorized", onUnauthorized);
  }, []);

  const login = useCallback(async (email: string, password: string, companySlug?: string) => {
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, companySlug }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        return { ok: false as const, message: body?.message || "Login failed" };
      }
      try { 
        localStorage.setItem(TOKEN_KEY, body.token);
        if (body.user) localStorage.setItem(USER_KEY, JSON.stringify(body.user));
      } catch {}
      setToken(body.token);
      setUser(body.user);
      return { ok: true as const, user: body.user, token: body.token };
    } catch (err: any) {
      return { ok: false as const, message: err?.message || "Network error" };
    }
  }, []);

  const logout = useCallback(() => {
    try { 
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } catch {}
    setToken(null);
    setUser(null);
    fetch("/api/auth/logout", { method: "POST" }).catch(() => {});
  }, []);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const me = await fetchMe(token);
      if (me) {
        setUser(me);
        try { localStorage.setItem(USER_KEY, JSON.stringify(me)); } catch {}
      }
    } catch {
      logout();
    }
  }, [token, fetchMe, logout]);

  const value: AuthContextValue = {
    user,
    token,
    isLoading,
    isAuthenticated: Boolean(token && user),
    login,
    logout,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}
